import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactDropzone from 'react-dropzone'
import isEmpty from 'ramda/src/isEmpty'
import { Error, Label, Spinner } from '..'
import Image from './assets/pdf.svg'
import { head, includes } from 'ramda'
import { getMimeTypeByBuffer, validateMimeByString, validateSize } from './imageChecker'
import { MIN_IMAGE_SIZE, MAX_IMAGE_SIZE } from '../../constants/constants'

import cn from 'classnames'
import styles from './style.css'

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const FORBIDDENT_EXTENSIONS = ['jfif']
/**
 *
 * @param {DocMimeType} mimeType
 */
const isValidMime = (mimeType) => includes(mimeType, ACCEPTED_MIME_TYPES)
/**
 * @param {function} onDropReject
 * @param {function} onDropAccept
 * @param {string} label
 * @param {Blob} photo
 * @param {?boolean} multiple
 * @param {string} uploadLabel
 * @param {string} imgAlt
 * @param {string} changeLabel
 * @param {string} name
 * @param {string} error
 * @param {string | string[]} acceptedMimeTypes
 * @param {boolean} valid
 * @param {?boolean} isLoading
 */
const DropZone = ({
  onDropReject,
  onDropAccept,
  label,
  photo,
  multiple,
  uploadLabel,
  imgAlt,
  changeLabel,
  name,
  error,
  acceptedMimeTypes,
  valid,
  isLoading,
  onRemoveUploaded
}) => {
  const { t } = useTranslation()
  const dropzoneRef = useRef(null)

  /**
   * @param {string} name
   * @returns {function}
   */
  const onDrop = (name) =>
    /**
    * @param {FileImg[]} accepted
   * @param {FileImg} rejected
   */
    (accepted, rejected) => {
      const blobReader = new FileReader()
      let mimeType = ''

      /**
       * @param {{SyntheticEvent}} param
       */
      blobReader.onloadend = ({ currentTarget }) => {
        const buffer = new Uint8Array(currentTarget.result).subarray(0, 4)
        mimeType = getMimeTypeByBuffer(buffer)
        const hasForbiddenExtension = FORBIDDENT_EXTENSIONS.includes(accepted[0].name.split('.').pop().toLowerCase())
        if (isValidMime(mimeType) && !hasForbiddenExtension) {
          return onDropAccept(name, head(accepted), blobReader.result)
        }

        return onDropReject(name, 'verifyId.wrongMimeType')
      }

      if (!isEmpty(rejected)) {
        const { size, type } = head(rejected)
        const message = onDropRejectMessage(size, type)
        onDropReject(name, message)
      }

      if (!isEmpty(accepted)) {
        blobReader.readAsArrayBuffer(head(accepted))
      }
    }

  /**
   * @param {number} size
   * @param {string} type
   * @returns {string}
   */
  const onDropRejectMessage = (size, type) => {
    let message = ''

    if (!validateMimeByString(type, ACCEPTED_MIME_TYPES)) {
      message = 'verifyId.wrongMimeType'
    }

    if (!validateSize(size, MIN_IMAGE_SIZE, MAX_IMAGE_SIZE)) {
      message = 'verifyId.wrongfileSize'
    }

    return message
  }

  const isPdf = !isEmpty(photo) && photo.type.indexOf('pdf') !== -1

  const onUploadChangeImageHandler = () => {
    dropzoneRef.current.open()
  }

  const onRemoveImageHandler = () => {
    onRemoveUploaded(name)
  }

  return (
    <div id='dropZoneBlock' className={styles.field}>
      <Label htmlFor={t(label)} value={t(label)} valid={valid} info={t('dropzone.restriction')} />
      <div className={styles.dropzone}>
        <ReactDropzone
          ref={dropzoneRef}
          onDrop={onDrop(name)}
          multiple={multiple}
          accept={acceptedMimeTypes}
          maxSize={MAX_IMAGE_SIZE}
          minSize={MIN_IMAGE_SIZE}
          name={name}
        >
          <section className={styles.container}>
            {!isPdf && !isEmpty(photo) && !isLoading && (
              <img src={URL.createObjectURL(photo)} className={styles.preview} alt={t(imgAlt)} />
            )}
            {isPdf && !isEmpty(photo) && !isLoading &&
              <img className={styles.pdfIcon} src={Image} />}
            {isLoading && <Spinner className={cn(styles.spinner, styles.dropzoneSpinner)} isSmall />}
          </section>
        </ReactDropzone>
        {!isLoading &&
          <div className={cn(styles.pseudoButtonContainer, {
            [styles.uploaded]: !isEmpty(photo)
          })}
          >
            <button type='button' disabled={isLoading} className={styles.pseudoButton} onClick={onUploadChangeImageHandler}>
              {t(isEmpty(photo) ? uploadLabel : changeLabel)}
            </button>
            {!isEmpty(photo) && (
              <button type='button' disabled={isLoading} className={cn(styles.pseudoButton, styles.removeImageButton)} onClick={onRemoveImageHandler}>
                {t('dropzone.removeImage')}
              </button>
            )}
          </div>}
      </div>
      <Error value={error} />
    </div>
  )
}

DropZone.defaultProps = {
  label: 'dropzone.dropFile',
  uploadLabel: 'dropzone.uploadImage',
  changeLabel: 'dropzone.changeImage',
  imgAlt: 'dropzone.preview',
  name: 'dropzone',
  maxImageSize: 1000000,
  acceptedMimeTypes: ['image/jpeg', 'image/png', 'application/pdf'],
  multiple: false,
  isLoading: false,
  photo: {}
}

export { DropZone }
